<template>
	<div class="section">
		<div class="container">
			<b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false"></b-loading>
			<div v-if="!isLoading">
				<div class="columns has-text-centered">
					<div class="column is-10 is-offset-1">
						<h1>{{ ReportA.Name }} vs {{ ReportB.Name }} Comparison report</h1>
						<p>Created: {{ $moment().format("DD MMMM, YYYY") }}</p>
					</div>
				</div>

				<h2>Report Sections</h2>
				<ul class="sections">
					<li><a href="#emissions">Aggregate annual CO2 emissions</a></li>
					<li><a href="#type">Analysis by aircraft type</a></li>
					<li><a href="#model">Analysis by aircraft model</a></li>
				</ul>

				<a class="anchor" id="emissions"></a>
				<div class="my-6">
					<h2 class="my-4" id="emissions">Aggregate annual CO2 Emissions</h2>
					<div class="columns">
						<div class="column">
							<div class="is-chart mb-2">
								<h3 class="my-4">{{ ReportA.Name }} portfolio</h3>
								<span class="large-font is-blue">{{ $math.format($math.round((ReportA.Emissions || 0), 3)) }}</span> tonnes
							</div>
						</div>
						<div class="column">
							<div class="is-chart mb-2">
								<h3 class="my-4">{{ ReportB.Name }} portfolio</h3>
								<span class="large-font is-red">{{ $math.format($math.round((ReportB.Emissions || 0), 3)) }}</span> tonnes
							</div>
						</div>
					</div>
					<p class="has-text-centered">Actual CO2 emissions may vary, please see the <a target="_blank" href="/Calculation Methodology Document.pdf">Calculation Methodology Document</a> for more deatils.</p>

				</div>

				<a class="anchor" id="type"></a>
				<div class="my-6">

					<h2>Analysis by aircraft type</h2>
					<div class="my-6">
						<h3 class="has-text-centered is-blue">{{ ReportA.Name }} portfolio by aircraft type</h3>
						<AircraftTable :Aircraft="ReportA.AircraftByType"
									   IsBlue
									   HideNumber
									   Type></AircraftTable>
					</div>
					<div class="my-6">
						<h3 class="has-text-centered is-red">{{ ReportB.Name }} portfolio by aircraft type</h3>
						<AircraftTable :Aircraft="ReportB.AircraftByType"
									   IsRed
									   HideNumber
									   Type></AircraftTable>
					</div>
				</div>

				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionBarCharts']" :disabled="exporting['aircraftByTypeEmissionBarCharts']" @click="Export('aircraftByTypeEmissionBarCharts')">Export chart</b-button>
					<div id="aircraftByTypeEmissionBarCharts" class="clear">
						<h3 class="chart-title my-4 has-text-centered">Portfolio comparison by aircraft type of cumulative CO2 emissions</h3>
						<div class="columns has-text-centered">
							<div class="column">
								<bar :Data="ReportA.AircraftByTypeEmissionBarChart" :Options="chartOptions.bar.typeEmission.options"></bar>
							</div>
							<div class="column">
								<bar :Data="ReportB.AircraftByTypeEmissionBarChart" :Options="chartOptions.bar.typeEmission.options"></bar>
							</div>
						</div>
					</div>
				</div>
				<br />
				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionRadarChart']" :disabled="exporting['aircraftByTypeEmissionRadarChart']" @click="Export('aircraftByTypeEmissionRadarChart')">Export chart</b-button>
					<div id="aircraftByTypeEmissionRadarChart" class="clear">

						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft type of cumulative CO2 emissions</h3>
						<radar :Data="comparison.AircraftByTypeEmissionRadarChart" :Options="chartOptions.radar.typeEmission.options" :height="500"></radar>

					</div>
				</div>
				<br />
				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionPieCharts']" :disabled="exporting['aircraftByTypeEmissionPieCharts']" @click="Export('aircraftByTypeEmissionPieCharts')">Export chart</b-button>
					<div id="aircraftByTypeEmissionPieCharts" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft type of cumulative CO2 emissions</h3>
						<div class="columns has-text-centered">
							<div class="column">
								<h3 class="my-3 chart-title">{{ ReportA.Name }}</h3>
								<pie :Data="ReportA.AircraftByTypeEmissionPieChart" :Options="chartOptions.pie.typeEmission.options"></pie>
							</div>
							<div class="column">
								<h3 class="my-3 chart-title">{{ ReportB.Name }}</h3>
								<pie :Data="ReportB.AircraftByTypeEmissionPieChart" :Options="chartOptions.pie.typeEmission.options"></pie>
							</div>
						</div>
					</div>
				</div>


				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionScatterChart']" :disabled="exporting['aircraftByTypeEmissionScatterChart']" @click="Export('aircraftByTypeEmissionScatterChart')">Export chart</b-button>
					<div id="aircraftByTypeEmissionScatterChart" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft type showing aggregate annual CO2 emissions against average stage length</h3>
						<scatter :Data="comparison.AircraftByTypeEmissionScatterChart" :Options="chartOptions.scatter.typeEmission.options"></scatter>
					</div>
				</div>

				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionBubbleChart']" :disabled="exporting['aircraftByTypeEmissionBubbleChart']" @click="Export('aircraftByTypeEmissionBubbleChart')">Export chart</b-button>
					<div id="aircraftByTypeEmissionBubbleChart" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft type showing average cycles against average flight hours against aggregate annual CO2 emissions</h3>
						<bubble :Data="comparison.AircraftByTypeEmissionBubbleChart" :Options="chartOptions.bubble.typeEmission.options"></bubble>
					</div>
				</div>

				<a class="anchor" id="model"></a>
				<div class="my-6">

					<h2>Analysis by aircraft model</h2>
					<div class="my-6">
						<h3 class="has-text-centered is-blue">{{ ReportA.Name }} portfolio by aircraft model</h3>

						<AircraftTable :Aircraft="ReportA.AircraftByModel"
									   IsBlue
									   HideNumber
									   Model></AircraftTable>
					</div>
					<div class="my-6">
						<h3 class="has-text-centered is-red">{{ ReportB.Name }} portfolio by aircraft model</h3>
						<AircraftTable :Aircraft="ReportB.AircraftByModel"
									   IsRed
									   HideNumber
									   Model></AircraftTable>
					</div>
				</div>

				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionPieChart']" :disabled="exporting['aircraftByModelEmissionPieChart']" @click="Export('aircraftByModelEmissionPieChart')">Export chart</b-button>
					<div id="aircraftByModelEmissionPieChart" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft model of cumulative CO2 emissions</h3>
						<div class="columns has-text-centered">
							<div class="column">
								<h3 class="my-3 chart-title">{{ ReportA.Name }}</h3>
								<pie :Data="ReportA.AircraftByModelEmissionPieChart" :Options="chartOptions.pie.modelEmission.options"></pie>
							</div>
							<div class="column">
								<h3 class="my-3 chart-title">{{ ReportB.Name }}</h3>
								<pie :Data="ReportB.AircraftByModelEmissionPieChart" :Options="chartOptions.pie.modelEmission.options"></pie>
							</div>
						</div>
					</div>
				</div>
				<br />

				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionScatterChart']" :disabled="exporting['aircraftByModelEmissionScatterChart']" @click="Export('aircraftByModelEmissionScatterChart')">Export chart</b-button>
					<div id="aircraftByModelEmissionScatterChart" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft model showing aggregate annual CO2 emissions agains average stage length</h3>
						<scatter :Data="comparison.AircraftByModelEmissionScatterChart" :Options="chartOptions.scatter.modelEmission.options"></scatter>
					</div>
				</div>


				<div class="is-chart">
					<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionBubbleChart']" :disabled="exporting['aircraftByModelEmissionBubbleChart']" @click="Export('aircraftByModelEmissionBubbleChart')">Export chart</b-button>
					<div id="aircraftByModelEmissionBubbleChart" class="clear">
						<h3 class="chart-title my-6 has-text-centered">Portfolio comparison by aircraft model showing average cycles against average flight hours against aggregate annual CO2 emissions</h3>
						<bubble :Data="comparison.AircraftByModelEmissionBubbleChart" :Options="chartOptions.bubble.modelEmission.options"></bubble>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	var $math;
	import Bar from "@/components/chart/barChart.vue";
	import Radar from "@/components/chart/radarChart.vue";
	import Pie from "@/components/chart/pieChart.vue";
	import Scatter from "@/components/chart/scatterChart.vue";
	import Bubble from "@/components/chart/bubbleChart.vue";
	import AircraftTable from "@/components/aircraft/aircraftTable.vue";
	export default {
		components: {
			AircraftTable,
			"bar": Bar,
			"radar": Radar,
			"pie": Pie,
			"scatter": Scatter,
			"bubble": Bubble
		},
		computed: {
			ReportA() {
				return this.comparison && this.comparison.Reports ? this.comparison.Reports[0] : {};
			},
			ReportB() {
				return this.comparison && this.comparison.Reports ? this.comparison.Reports[1] : {};
			}
		},
		data() {
			return {
				myStyles: {
					height: `300px`,
					width: '500px',
					marginLeft: '50px'
				},
				isLoading: true,
				exporting: {},
				comparison: {},
				chartOptions: {
					pie: {
						typeEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										label: (item, data) => { return data.labels[item.index] + "  " + $math.format($math.round(data.datasets[0].data[item.index], 3)) + " tonnes" }
									}
								},
								legend: {
									display: false
								},

							}
						},
						modelEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										label: (item, data) => { return data.labels[item.index] + "  " + $math.format($math.round(data.datasets[0].data[item.index], 3)) + " tonnes" }
									}
								},
								legend: {
									display: false
								},

							}
						}
					},
					bar: {
						typeEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[item.index], 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										stacked: true,
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)',
											fontSize: 16
										},
										stacked: true,
									}]
								},
								legend: {
									display: false,
								},
							}
						}
					},
					radar: {
						typeEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[item.index], 3)) + " tonnes"; }
									}
								},
								scale: {
									ticks: {
										beginAtZero: true
									}
								},
								legend: {
									display: false
								},

							}
						},
					},
					scatter: {
						typeEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " stage length | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average stage length'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false,
								},
								elements: {
									point: {
										radius: 5
									}
								}
							}
						},
						modelEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " stage length | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average stage length',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false,
								},
								elements: {
									point: {
										radius: 5
									}
								}
							}
						}
					},
					bubble: {
						typeEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " flight hours | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 2)) + " cycles | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].q, 3)) + " tonnes"
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average flight hours',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average cycles',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false,
								},
								elements: {
									point: {
										radius: function (context) {
											var value = context.dataset.data[context.dataIndex];
											const q = value.q / context.dataset.Normalizer;
											if (q < 1)
												return 1;
											else
												return q;
										}
									}
								}
							}
						},
						modelEmission: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " flight hours | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 2)) + " cycles | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].q, 3)) + " tonnes"
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average flight hours',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average cycles',
											fontSize: 16
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false,
								},
								elements: {
									point: {
										radius: function (context) {
											var value = context.dataset.data[context.dataIndex];
											const q = value.q / context.dataset.Normalizer;
											if (q < 1)
												return 1;
											else
												return q;
										}
									}
								}
							}
						}
					}
				},
			}
		},
		mounted() {
			$math = this.$math;
			const idOne = this.$route.params.idOne;
			const idTwo = this.$route.params.idTwo;
			this.GetComparisonReport(idOne, idTwo);
		},
		methods: {
			async Export(id) {
				this.$set(this.exporting, id, true);
				await this.$export(id);
				this.exporting[id] = false;
			},
			async GetComparisonReport(idOne, idTwo) {
				try {
					var response = await this.$http.get("/api/portfolio/" + idOne + "/" + idTwo + "/compare");
					this.comparison = this.AddColor(response.data);
					this.isLoading = false;
					return response;
				}
				catch (err) {
					console.error(err);
					return Promise.resolve();
				}
			},
			AddColor(comparison) {
				var self = this;
				for (var i in comparison.Reports[0].AircraftByTypeEmissionBarChart.datasets) {
					const dataset = comparison.Reports[0].AircraftByTypeEmissionBarChart.datasets[i];
					dataset.backgroundColor = self.$getColors(0, i);
					dataset.borderColor = self.$getColors(0, i);
				}

				for (var i in comparison.Reports[1].AircraftByTypeEmissionBarChart.datasets) {
					const dataset = comparison.Reports[1].AircraftByTypeEmissionBarChart.datasets[i];
					dataset.backgroundColor = self.$getColors(1, i);
					dataset.borderColor = self.$getColors(1, i);
				}

				comparison.Reports[0].AircraftByTypeEmissionPieChart.datasets.forEach(function (d, i) {
					d.backgroundColor = self.$getColors(0, null);
				})

				comparison.Reports[1].AircraftByTypeEmissionPieChart.datasets.forEach(function (d, i) {
					d.backgroundColor = self.$getColors(1, null);
				})

				comparison.AircraftByTypeEmissionRadarChart.datasets.forEach(function (d, i) {
					d.borderColor = i == 0 ? self.$getColors(0, i) : self.$getColors(1, i);
					d.backgroundColor = 'transparent';
					d.borderWidth = 6;
				})

				comparison.AircraftByTypeEmissionBubbleChart.datasets.forEach(function (d, i) {
					d.borderColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.backgroundColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i, 0.4) : self.$getColors(1, i, 0.4);
					d.borderWidth = 1;
				});

				comparison.AircraftByTypeEmissionScatterChart.datasets.forEach(function (d, i) {
					d.borderColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.backgroundColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.borderWidth = 1;
				});

				comparison.Reports[0].AircraftByModelEmissionPieChart.datasets.forEach(function (d, i) {
					d.backgroundColor = self.$getColors(0, null);
				})

				comparison.Reports[1].AircraftByModelEmissionPieChart.datasets.forEach(function (d, i) {
					d.backgroundColor = self.$getColors(1, null);
				})

				comparison.AircraftByModelEmissionScatterChart.datasets.forEach(function (d, i) {
					d.borderColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.backgroundColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.borderWidth = 1;
				});

				comparison.AircraftByModelEmissionBubbleChart.datasets.forEach(function (d, i) {
					d.borderColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i) : self.$getColors(1, i);
					d.backgroundColor = d.label.includes(comparison.Reports[0].Name) ? self.$getColors(0, i, 0.4) : self.$getColors(1, i, 0.4);
					d.borderWidth = 1;
				});

				return comparison;
			},
		}
	}

</script>

<style>

	.type-emissions {
		text-align: center;
	}

		.type-emissions canvas {
			margin: 0 auto;
		}
</style>
